import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/App";
import axios from "axios";
import {
  Wrench,
  Briefcase,
  Clock,
  CheckCircle2,
  DollarSign,
  FileText,
  MapPin,
  AlertTriangle,
  Loader2,
  LogOut,
  ChevronRight,
  Building2,
  Calendar,
  Send,
  Shield,
  Users,
  UserPlus,
  User,
  Trash2,
  Edit,
  Mail,
  Phone,
  Eye,
  HelpCircle,
  Settings,
  Package,
  Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import TelegramConnect from "@/components/TelegramConnect";
import TelegramInviteBanner from "@/components/TelegramInviteBanner";
import ContractorSettingsModal from "@/components/ContractorSettingsModal";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isaac-platform/artifacts/9qsxumik_Sterile%20Mate%20Logo%20%282%29.jpg";

export default function ContractorDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("jobs");
  const [showSettings, setShowSettings] = useState(false);
  
  // Team Management State
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState({ 
    name: "", 
    email: "", 
    phone: "", 
    role: "field_tech",
    access_level: "view_only",
    can_submit_certificates: false,
    can_view_billing: false,
    can_manage_team: false
  });
  const [savingMember, setSavingMember] = useState(false);

  // Get token from localStorage
  const token = localStorage.getItem("contractor_token");

  useEffect(() => {
    if (!token) {
      navigate("/contractor/login");
      return;
    }
    fetchDashboard();
  }, [navigate, token]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/contractor/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("contractor_token");
        localStorage.removeItem("contractor_data");
        navigate("/contractor/login");
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("contractor_token");
    localStorage.removeItem("contractor_data");
    navigate("/contractor/login");
  };

  const fetchTeam = async () => {
    try {
      setLoadingTeam(true);
      const res = await axios.get(`${API}/contractor/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(res.data.team || []);
    } catch (err) {
      console.error("Failed to fetch team:", err);
    } finally {
      setLoadingTeam(false);
    }
  };

  const handleSaveMember = async () => {
    if (!memberForm.name || !memberForm.email) {
      toast.error("Name and email are required");
      return;
    }
    
    try {
      setSavingMember(true);
      
      if (editingMember) {
        // Update existing member
        await axios.put(`${API}/contractor/team/${editingMember.id}`, memberForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Team member updated");
      } else {
        // Add new member
        await axios.post(`${API}/contractor/team`, memberForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Team member added");
      }
      
      setShowAddMember(false);
      setEditingMember(null);
      setMemberForm({ name: "", email: "", phone: "", role: "field_tech" });
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save team member");
    } finally {
      setSavingMember(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!confirm("Are you sure you want to remove this team member?")) return;
    
    try {
      await axios.delete(`${API}/contractor/team/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Team member removed");
      fetchTeam();
    } catch (err) {
      toast.error("Failed to remove team member");
    }
  };

  const openEditMember = (member) => {
    setMemberForm({
      name: member.name,
      email: member.email,
      phone: member.phone || "",
      role: member.role
    });
    setEditingMember(member);
    setShowAddMember(true);
  };

  const getRoleBadge = (role) => {
    const styles = {
      view_only: { bg: "bg-slate-100", text: "text-slate-600", label: "View Only" },
      field_tech: { bg: "bg-blue-100", text: "text-blue-700", label: "Field Tech" },
      manager: { bg: "bg-purple-100", text: "text-purple-700", label: "Manager" }
    };
    return styles[role] || styles.view_only;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) return null;

  const { contractor, jobs, invoices, earnings, job_counts } = data;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Sterile Mate" className="h-10 object-contain" />
            <div className="hidden sm:block">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-slate-900">{contractor.company_name}</p>
                {data?.is_dual_role && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-medium rounded-full">
                    <Star className="w-3 h-3" />
                    Dual-Role
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500">{contractor.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!contractor.is_compliant && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>Compliance Issue</span>
              </div>
            )}
            <TelegramConnect token={token} userType="contractor" />
            <Link to="/contractor/profile">
              <Button 
                variant="outline" 
                size="sm"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="contractor-profile-btn"
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </Button>
            </Link>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowSettings(true)}
              data-testid="contractor-settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Fortress Certification Banner */}
        {!contractor.is_compliant && (
          <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4" data-testid="fortress-certification-banner">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">Become Fortress Certified</h3>
                <p className="text-amber-700 text-sm mt-1">
                  Complete your profile and upload your credentials to appear in hospital search results.
                </p>
              </div>
              <Link to="/contractor/profile">
                <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                  Complete Profile
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Telegram Invite Banner - shows once per session for non-connected users */}
        <TelegramInviteBanner 
          token={token} 
          userType="contractor" 
          userName={data?.contractor?.company_name || data?.contractor?.name?.split(' ')[0]}
        />
        
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{job_counts.new}</p>
                <p className="text-sm text-slate-500">New Jobs</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{job_counts.in_progress}</p>
                <p className="text-sm text-slate-500">In Progress</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{job_counts.completed}</p>
                <p className="text-sm text-slate-500">Completed</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">${earnings.total.toFixed(0)}</p>
                <p className="text-sm text-slate-500">Total Earned</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 mb-4">
            <TabsTrigger value="jobs" className="flex items-center gap-1">
              <Wrench className="w-4 h-4" />
              Jobs
            </TabsTrigger>
            {data?.is_dual_role && (
              <TabsTrigger value="supply-chain" className="flex items-center gap-1">
                <Package className="w-4 h-4" />
                Supply Chain
              </TabsTrigger>
            )}
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="earnings">Earnings</TabsTrigger>
            <TabsTrigger value="team" onClick={() => { if (teamMembers.length === 0) fetchTeam(); }}>Team</TabsTrigger>
            <TabsTrigger value="help">
              <HelpCircle className="w-4 h-4 mr-1" />
              Help
            </TabsTrigger>
          </TabsList>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <div className="space-y-6">
              {/* New Jobs */}
              {jobs.new.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-6 py-4 bg-blue-50 border-b border-blue-100">
                    <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      New Assignments ({jobs.new.length})
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {jobs.new.map(job => (
                      <JobCard key={job.id} job={job} type="new" />
                    ))}
                  </div>
                </div>
              )}

              {/* In Progress */}
              {jobs.in_progress.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-6 py-4 bg-amber-50 border-b border-amber-100">
                    <h3 className="font-semibold text-amber-900 flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      In Progress ({jobs.in_progress.length})
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {jobs.in_progress.map(job => (
                      <JobCard key={job.id} job={job} type="in_progress" />
                    ))}
                  </div>
                </div>
              )}

              {/* Completed */}
              {jobs.completed.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-6 py-4 bg-green-50 border-b border-green-100">
                    <h3 className="font-semibold text-green-900 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      Completed ({jobs.completed.length})
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {jobs.completed.slice(0, 5).map(job => (
                      <JobCard key={job.id} job={job} type="completed" />
                    ))}
                  </div>
                </div>
              )}

              {jobs.new.length === 0 && jobs.in_progress.length === 0 && jobs.completed.length === 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900">No Jobs Yet</h3>
                  <p className="text-slate-500 mt-1">New assignments will appear here when dispatched.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Supply Chain Tab (Dual-Role Only) */}
          {data?.is_dual_role && (
            <TabsContent value="supply-chain">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Package className="w-5 h-5 text-green-600" />
                      Supply Chain Dashboard
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">Manage backorder quotes and inventory</p>
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-medium rounded-full">
                    <Star className="w-3 h-3" />
                    Dual-Role Access
                  </span>
                </div>
                <div className="p-6">
                  <div className="text-center py-8">
                    <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h4 className="font-semibold text-slate-800 mb-2">Supply Chain Features</h4>
                    <p className="text-slate-500 mb-4 max-w-md mx-auto">
                      As a dual-role entity, you can now respond to backorder RFQs, upload parts catalogs, and submit quotes.
                    </p>
                    {data?.linked_supplier_id && (
                      <Link 
                        to={`/supplier/dashboard`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Package className="w-4 h-4" />
                        Open Full Supplier Portal
                      </Link>
                    )}
                    {!data?.linked_supplier_id && (
                      <p className="text-sm text-amber-600 mt-4">
                        Your supplier profile is being set up. Please check back shortly.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
          )}

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">Invoice History</h3>
              </div>
              {invoices.length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No invoices submitted yet</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {invoices.map(inv => (
                    <div key={inv.id} className="px-6 py-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">Ticket #{inv.ticket_id}</p>
                        <p className="text-sm text-slate-500">{inv.description}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Submitted: {new Date(inv.submitted_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-900">${inv.amount?.toFixed(2)}</p>
                        <span className={`text-xs px-2 py-1 rounded ${
                          inv.status === "paid" ? "bg-green-100 text-green-700" :
                          inv.status === "approved" ? "bg-blue-100 text-blue-700" :
                          inv.status === "rejected" ? "bg-red-100 text-red-700" :
                          "bg-amber-100 text-amber-700"
                        }`}>
                          {inv.status?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Earnings Tab */}
          <TabsContent value="earnings">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Earnings Summary</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                    <span className="text-slate-600">Pending Review</span>
                    <span className="font-bold text-amber-700">${earnings.pending.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-slate-600">Approved (Awaiting Payment)</span>
                    <span className="font-bold text-blue-700">${earnings.approved.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <span className="text-slate-600">Paid</span>
                    <span className="font-bold text-green-700">${earnings.paid.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-slate-900">Total Earned (YTD)</span>
                      <span className="text-2xl font-bold text-emerald-600">${earnings.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Payment Information</h3>
                <div className="space-y-3 text-sm">
                  <p className="text-slate-600">
                    <strong>Payment Terms:</strong> Net 30 from invoice approval
                  </p>
                  <p className="text-slate-600">
                    <strong>Payment Method:</strong> Direct deposit or check
                  </p>
                  <p className="text-slate-600">
                    <strong>1099 Reporting:</strong> Annual tax documents available in January
                  </p>
                </div>
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    For payment questions, contact:<br />
                    <strong>accounts@sterilemate.com</strong><br />
                    800.988.3892
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Team Members
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">Manage your field technicians and team access</p>
                </div>
                <Button onClick={() => { setEditingMember(null); setMemberForm({ name: "", email: "", phone: "", role: "field_tech" }); setShowAddMember(true); }} className="bg-blue-600 hover:bg-blue-700">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Member
                </Button>
              </div>
              
              {loadingTeam ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="p-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-slate-900">No Team Members Yet</h4>
                  <p className="text-slate-500 mt-1">Add team members to give them access to jobs and updates.</p>
                  <Button className="mt-4 bg-blue-600 hover:bg-blue-700" onClick={() => setShowAddMember(true)}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Your First Team Member
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {teamMembers.map((member) => {
                    const roleBadge = getRoleBadge(member.role);
                    return (
                      <div key={member.id} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                            <span className="font-medium text-slate-600">{member.name?.charAt(0)?.toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{member.name}</p>
                            <div className="flex items-center gap-3 text-sm text-slate-500 mt-0.5">
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {member.email}
                              </span>
                              {member.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {member.phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${roleBadge.bg} ${roleBadge.text}`}>
                            {roleBadge.label}
                          </span>
                          <Button variant="ghost" size="sm" onClick={() => openEditMember(member)}>
                            <Edit className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteMember(member.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Role Descriptions */}
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Role Permissions Guide</h4>
                <div className="grid md:grid-cols-4 gap-4 text-sm">
                  <div className="flex items-start gap-2">
                    <Eye className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700">View Only</p>
                      <p className="text-slate-500">Can see jobs assigned to company</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Wrench className="w-4 h-4 text-blue-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700">Field Tech</p>
                      <p className="text-slate-500">Update status, upload photos</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-purple-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700">Manager</p>
                      <p className="text-slate-500">Assign jobs, manage credentials</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-emerald-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700">Admin</p>
                      <p className="text-slate-500">Full access - office manager</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Add/Edit Member Dialog */}
            <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-blue-600" />
                    {editingMember ? "Edit Team Member" : "Add Team Member"}
                  </DialogTitle>
                  <DialogDescription>
                    {editingMember ? "Update team member information and role" : "Add a new team member to your organization"}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input 
                      value={memberForm.name}
                      onChange={(e) => setMemberForm({...memberForm, name: e.target.value})}
                      placeholder="John Smith"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Email Address *</Label>
                    <Input 
                      type="email"
                      value={memberForm.email}
                      onChange={(e) => setMemberForm({...memberForm, email: e.target.value})}
                      placeholder="john@company.com"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Phone Number</Label>
                    <Input 
                      type="tel"
                      value={memberForm.phone}
                      onChange={(e) => setMemberForm({...memberForm, phone: e.target.value})}
                      placeholder="800-988-3892"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Role *</Label>
                    <Select value={memberForm.role} onValueChange={(v) => {
                      // Auto-set permissions based on role
                      const permissions = {
                        view_only: { access_level: "view_only", can_submit_certificates: false, can_view_billing: false, can_manage_team: false, can_edit_credentials: false, can_manage_quickbids: false, can_edit_profile: false },
                        field_tech: { access_level: "edit_assigned", can_submit_certificates: true, can_view_billing: false, can_manage_team: false, can_edit_credentials: false, can_manage_quickbids: false, can_edit_profile: false },
                        manager: { access_level: "edit_all", can_submit_certificates: true, can_view_billing: true, can_manage_team: true, can_edit_credentials: true, can_manage_quickbids: true, can_edit_profile: false },
                        admin: { access_level: "full_access", can_submit_certificates: true, can_view_billing: true, can_manage_team: true, can_edit_credentials: true, can_manage_quickbids: true, can_edit_profile: true }
                      };
                      setMemberForm({...memberForm, role: v, ...permissions[v]});
                    }}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="view_only">View Only - Can see jobs</SelectItem>
                        <SelectItem value="field_tech">Field Tech - Update status & photos</SelectItem>
                        <SelectItem value="manager">Manager - Full access & team management</SelectItem>
                        <SelectItem value="admin">Admin - Full control (office manager)</SelectItem>
                      </SelectContent>
                    </Select>
                    {memberForm.role === "admin" && (
                      <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Admin can upload credentials while you&apos;re in the field
                      </p>
                    )}
                  </div>

                  {/* Permission Toggles */}
                  <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
                    <p className="text-sm font-medium text-slate-700">Permission Toggles</p>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">Submit Completion Certificates</p>
                        <p className="text-xs text-slate-500">Can submit work completion certificates</p>
                      </div>
                      <Switch 
                        checked={memberForm.can_submit_certificates}
                        onCheckedChange={(v) => setMemberForm({...memberForm, can_submit_certificates: v})}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">View Billing & Invoices</p>
                        <p className="text-xs text-slate-500">Access to financial information</p>
                      </div>
                      <Switch 
                        checked={memberForm.can_view_billing}
                        onCheckedChange={(v) => setMemberForm({...memberForm, can_view_billing: v})}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">Manage Team</p>
                        <p className="text-xs text-slate-500">Can add/edit team members</p>
                      </div>
                      <Switch 
                        checked={memberForm.can_manage_team}
                        onCheckedChange={(v) => setMemberForm({...memberForm, can_manage_team: v})}
                      />
                    </div>
                    
                    {/* New Admin Delegation Permissions */}
                    <div className="pt-3 border-t border-slate-200">
                      <p className="text-xs font-medium text-violet-600 mb-3">Admin Delegation Permissions</p>
                      
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm font-medium text-slate-900">Edit Credentials & COI</p>
                          <p className="text-xs text-slate-500">Upload and manage license documents</p>
                        </div>
                        <Switch 
                          checked={memberForm.can_edit_credentials || false}
                          onCheckedChange={(v) => setMemberForm({...memberForm, can_edit_credentials: v})}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm font-medium text-slate-900">Manage QuickBids</p>
                          <p className="text-xs text-slate-500">Respond to hospital broadcast requests</p>
                        </div>
                        <Switch 
                          checked={memberForm.can_manage_quickbids || false}
                          onCheckedChange={(v) => setMemberForm({...memberForm, can_manage_quickbids: v})}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">Edit Company Profile</p>
                          <p className="text-xs text-slate-500">Update company info, logo, and AI profile</p>
                        </div>
                        <Switch 
                          checked={memberForm.can_edit_profile || false}
                          onCheckedChange={(v) => setMemberForm({...memberForm, can_edit_profile: v})}
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700">Access Level</Label>
                      <Select 
                        value={memberForm.access_level} 
                        onValueChange={(v) => setMemberForm({...memberForm, access_level: v})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="view_only">View Only</SelectItem>
                          <SelectItem value="edit_assigned">Edit Assigned Jobs</SelectItem>
                          <SelectItem value="edit_all">Edit All Jobs</SelectItem>
                          <SelectItem value="full_access">Full Access</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddMember(false)}>Cancel</Button>
                  <Button onClick={handleSaveMember} disabled={savingMember} className="bg-blue-600 hover:bg-blue-700">
                    {savingMember && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {editingMember ? "Save Changes" : "Add Member"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Help Tab */}
          <TabsContent value="help">
            <div className="space-y-6">
              {/* Quick Start Guide */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 bg-blue-50 border-b border-blue-100">
                  <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                    <HelpCircle className="w-5 h-5" />
                    Contractor Quick Start Guide
                  </h3>
                </div>
                <div className="p-6 space-y-6">
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">📋 How Jobs Work</h4>
                    <ol className="list-decimal list-inside space-y-2 text-slate-600 text-sm">
                      <li><strong>New Assignment</strong> - You receive a dispatch notification with job details</li>
                      <li><strong>Accept Job</strong> - Review details and click &quot;View & Accept&quot; to acknowledge</li>
                      <li><strong>Start Travel</strong> - When leaving for the site, update status to &quot;En Route&quot;</li>
                      <li><strong>Check In</strong> - Upon arrival, update to &quot;On Site&quot;</li>
                      <li><strong>Complete Work</strong> - After finishing, mark as &quot;Completed&quot;</li>
                      <li><strong>Upload Photos</strong> - Add completion photos as proof of work</li>
                      <li><strong>Get Sign-off</strong> - Have hospital staff sign the completion certificate</li>
                      <li><strong>Submit Invoice</strong> - Enter your charges and submit for payment</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">⚠️ Warranty Jobs</h4>
                    <p className="text-slate-600 text-sm">
                      Jobs marked with a yellow &quot;WARRANTY&quot; badge are covered under equipment warranty. 
                      Standard labor and parts charges cannot be billed. If additional work is needed 
                      outside warranty scope, contact your admin for an override code.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">👥 Team Management</h4>
                    <p className="text-slate-600 text-sm mb-2">
                      Add your field technicians to give them access to jobs:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-slate-600 text-sm">
                      <li><strong>View Only</strong> - See job details but cannot update</li>
                      <li><strong>Field Tech</strong> - Update job status and upload photos</li>
                      <li><strong>Manager</strong> - Full access including team management</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">💰 Payments</h4>
                    <p className="text-slate-600 text-sm">
                      Invoices are processed on Net 30 terms from approval date. 
                      View your earnings breakdown in the &quot;Earnings&quot; tab. 
                      1099 tax documents are available each January.
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Support */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Need Help?</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">
                      <strong>Technical Support</strong><br />
                      info@sterilemate.com<br />
                      800.988.3892 ext. 1
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">
                      <strong>Billing & Payments</strong><br />
                      accounts@sterilemate.com<br />
                      800.988.3892 ext. 2
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Settings Modal */}
      <ContractorSettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        token={token}
      />
    </div>
  );
}

// Job Card Component
function JobCard({ job, type }) {
  const ticket = job.ticket;
  const isWarranty = ticket?.is_warranty;

  return (
    <div className={`px-6 py-4 hover:bg-slate-50 transition-colors ${isWarranty ? "border-l-4 border-amber-500" : ""}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-slate-900">#{ticket?.id} - {ticket?.issue_type_label}</p>
            {isWarranty && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1">
                <Shield className="w-3 h-3" /> WARRANTY
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <Building2 className="w-4 h-4" />
              {ticket?.facility?.name}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {ticket?.facility?.address?.slice(0, 30)}...
            </span>
          </div>
          {ticket?.description && (
            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{ticket.description}</p>
          )}
          <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Dispatched: {new Date(job.sent_at).toLocaleDateString()}
          </p>
        </div>
        <Link to={`/contractor/job/${job.token}`}>
          <Button size="sm" variant={type === "new" ? "default" : "outline"} className={type === "new" ? "bg-blue-600 hover:bg-blue-700" : ""}>
            {type === "new" ? "View & Accept" : "View Details"}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
